import { useState, useRef, useEffect } from "react";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { postQuery } from "../services/BackendServices";
import "./ChatbotWidget.css";

const ChatbotWidget = ({ userType = "buyer" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState("start");
  const [userInput, setUserInput] = useState("");
  const [chatbotData, setChatbotData] = useState(null);
  const [collectedData, setCollectedData] = useState({});
  const messagesEndRef = useRef(null);

  // Load chatbot data when opened
  useEffect(() => {
    if (isOpen && !chatbotData) {
      loadChatbotData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadChatbotData = async () => {
    try {
      let data;
      if (userType === "global-partner") {
        data = await import("../services/chatbotData.globalpartner.json");
      } else {
        data = await import("../services/chatbotData.buyer.json");
      }
      setChatbotData(data.default);
      // Add initial message
      const startStep = data.default.start;
      setMessages([{ type: "bot", text: startStep.message, step: "start" }]);
    } catch (error) {
      console.error("Error loading chatbot data:", error);
    }
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleOptionClick = (option) => {
    // Add user message
    setMessages((prev) => [...prev, { type: "user", text: option.label }]);

    // Store data
    if (currentStep === "start") {
      setCollectedData((prev) => ({ ...prev, initialQuery: option.label }));
    }

    // Move to next step
    const nextStep = option.nextStep;
    setCurrentStep(nextStep);

    // Add bot response
    setTimeout(() => {
      const nextStepData = chatbotData[nextStep];
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: nextStepData.message, step: nextStep },
      ]);

      // Handle end step
      if (nextStepData.type === "end" && nextStepData.action === "submit") {
        submitData();
      }
    }, 500);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const currentStepData = chatbotData[currentStep];

    // Add user message
    setMessages((prev) => [...prev, { type: "user", text: userInput }]);

    // Store data based on step
    if (currentStep === "collect_contact") {
      setCollectedData((prev) => ({ ...prev, contact: userInput }));
    } else if (currentStep === "additional_query") {
      setCollectedData((prev) => ({
        ...prev,
        additionalQueries: [...(prev.additionalQueries || []), userInput],
      }));
    } else if (currentStep !== "start" && currentStep !== "check_more_queries") {
      setCollectedData((prev) => ({ ...prev, details: userInput }));
    }

    setUserInput("");

    // Move to next step
    const nextStep = currentStepData.nextStep;
    setCurrentStep(nextStep);

    // Add bot response
    setTimeout(() => {
      const nextStepData = chatbotData[nextStep];
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: nextStepData.message, step: nextStep },
      ]);

      // Handle end step
      if (nextStepData.type === "end" && nextStepData.action === "submit") {
        submitData();
      }
    }, 500);
  };

  const submitData = async () => {
    try {
      // Prepare conversation history
      const conversationHistory = messages.map((msg) => ({
        type: msg.type,
        message: msg.text,
        timestamp: new Date().toISOString(),
      }));

      await postQuery({
        target: "saveChatbotData",
        data: {
          userType,
          conversationHistory,
          collectedData,
          timestamp: new Date().toISOString(),
        },
      });
      console.log("Chat data submitted successfully");
    } catch (error) {
      console.error("Error submitting chat data:", error);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const currentStepData = chatbotData?.[currentStep];

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button className="chat-button" onClick={toggleChat}>
          <ChatIcon style={{ fontSize: "28px" }} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chat-header-title">
              <ChatIcon style={{ marginRight: "10px" }} />
              {userType === "global-partner" ? "Partner Support" : "Buyer Support"}
            </div>
            <button className="chat-close-btn" onClick={toggleChat}>
              <CloseIcon />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.type}-message`}>
                <div className="message-bubble">{msg.text}</div>
              </div>
            ))}

            {/* Options */}
            {currentStepData?.type === "options" && (
              <div className="chat-options">
                {currentStepData.options.map((option, index) => (
                  <button
                    key={index}
                    className="chat-option-btn"
                    onClick={() => handleOptionClick(option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {currentStepData?.type === "text" && (
            <form className="chat-input-form" onSubmit={handleTextSubmit}>
              <input
                type="text"
                className="chat-input"
                placeholder="Type your message..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                autoFocus
              />
              <button type="submit" className="chat-send-btn">
                <SendIcon />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
